import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUp, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useFlowBar } from '../hooks/useFlowBar';
import { useMotionPolicy } from '../hooks/useMotionPolicy';
import ExperienceBreadcrumbs from './ExperienceBreadcrumbs';

// The FlowBar (arch doc §4.2): a persistent, low-chrome orientation strip rendered once by
// Layout() under the fixed Header. Guarantees Back / Parent / Next are always reachable so
// no layout-bearing screen can trap the user. Light ivory/champagne lane (no dark bar, no
// maps, no fake metrics). All strings via t(); any motion gated by useMotionPolicy().
//
// Wave 1 keeps it fixed and height-stable; the mobile bottom "flow tray" form and
// scroll-collapse are deferred to a later visual pass (function is preserved on mobile via
// the top bar). Height is h-12 (48px); Layout reserves pt-12 so content clears it.
export default function FlowBar() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const model = useFlowBar();
  const motion = useMotionPolicy();

  const go = (anchor) => {
    if (anchor && anchor.path) navigate(anchor.path);
  };

  const { back, parent, next } = model;
  const hover = motion.animate ? 'transition-colors duration-150' : '';

  return (
    <nav
      aria-label={t('experience.flowbar.aria', 'Page navigation')}
      className="fixed left-0 right-0 top-16 z-40 h-12 border-b border-champagne-200 bg-ivory/85 backdrop-blur-md"
    >
      <div className="mx-auto flex h-full max-w-7xl items-center gap-1 px-3 sm:gap-2 sm:px-4">
        {back && back.path && (
          <button
            type="button"
            onClick={() => go(back)}
            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-slate-700 hover:bg-champagne-100 ${hover}`}
            title={back.label ? `${t('experience.flowbar.back', 'Back')}: ${back.label}` : t('experience.flowbar.back', 'Back')}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">{t('experience.flowbar.back', 'Back')}</span>
          </button>
        )}

        {parent && parent.path && (
          <button
            type="button"
            onClick={() => go(parent)}
            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-slate-700 hover:bg-champagne-100 ${hover}`}
            title={t('experience.flowbar.parent', 'Up a level')}
          >
            <ArrowUp className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">
              {t(parent.labelKey, t('experience.flowbar.parent', 'Up a level'))}
            </span>
          </button>
        )}

        {/* Breadcrumbs (center, desktop only) */}
        <div className="mx-1 hidden min-w-0 flex-1 md:block">
          <ExperienceBreadcrumbs />
        </div>
        <div className="flex-1 md:hidden" />

        {next && next.path && (
          <button
            type="button"
            onClick={() => go(next)}
            className={`inline-flex items-center gap-1 rounded-md bg-signal-blue px-3 py-1 text-sm font-medium text-white hover:opacity-90 ${hover}`}
            title={t(next.labelKey, t('experience.flowbar.next', 'Next step'))}
          >
            <span className="max-w-[9rem] truncate">
              {t(next.labelKey, t('experience.flowbar.next', 'Next step'))}
            </span>
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </nav>
  );
}
